/**
 * useWorkflowFilterSources - Listas de opções dos filtros do Workflow 2.0
 *
 * O catálogo de campos (`camposDoNegocio.ts`) declara, para cada campo
 * estruturado, uma `fonte` (workspaces, pipelines, fases, produtos, etiquetas,
 * origens de lead...). Este hook traduz essas chaves em listas prontas para os
 * seletores do construtor de filtros, REAPROVEITANDO os hooks já existentes de
 * cada módulo — sem novas RPCs e sem consultas duplicadas.
 *
 * Observação de performance: o hook é montado apenas dentro do modal de
 * filtros (sob demanda), evitando carregar catálogos com o designer fechado.
 *
 * @version 1.5.0
 * RELEASE: 2026-10-17 v1.5.0 - Fonte proposal_types (tipos de proposta, inclusive inativos
 *          para manter o rótulo de regras antigas), reaproveitando useCrmProposalTypes.
 * RELEASE: 2026-10-02 v1.3.0 - Fontes vehicle_statuses e checklist_templates (entidade Veículo),
 *          reaproveitadas de useWorkflowParamSources.
 * RELEASE: 2026-09-13 v1.2.0 - Recebe a entidade-raiz do processo e filtra as
 *          fontes de tarefa (categorias, classificações, fases e motivos) pelo
 *          módulo correspondente (Tarefa | Retenção | Jurídico).
 * RELEASE: 2026-09-12 v1.1.0 - Novas fontes do módulo de tarefas
 *          (task_categories, task_classifications, kanban_phases,
 *          task_completion_reasons), cobrindo as três entidades
 *          (Tarefa/Retenção/Jurídico) com agrupamento por módulo.
 * RELEASE: 2026-09-01 v1.0.0 - Criação inicial (filtros robustos do gatilho)
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useWorkflowParamSources } from '@/hooks/useWorkflowParamSources';
import { useDealTags } from '@/hooks/useDealTags';
import { useContactTags } from '@/hooks/useContactTags';
import { useLeadSources } from '@/hooks/useLeadSources';
import { useCrmLossReasons } from '@/hooks/useCrmLossReasons';
import { useCrmProducts } from '@/hooks/useCrmProducts';
import { useCrmCategories } from '@/hooks/useCrmCategories';
import { useCrmPriceTables } from '@/hooks/useCrmPriceTables';
// v1.5.0: tipos de proposta (lista oficial do CRM)
import { useCrmProposalTypes } from '@/hooks/useCrmProposalTypes';
// v1.1.0: fontes do módulo de tarefas (Tarefa/Retenção/Jurídico)
import { useTaskCategories } from '@/hooks/useTaskCategories';
import { useTaskPhases } from '@/hooks/useTaskPhases';
import { useTaskCompletionReasons } from '@/hooks/useTaskCompletionReasons';
import type { FonteDeOpcoes } from '@/components/workflow/inspector/camposDoNegocio';
// v1.2.0: tradução entidade-raiz -> tipo de tarefa (separação dos módulos)
import { taskTypeDaEntidade } from '@/components/workflow/taskEntity';
// v1.4.0: listas do módulo Ordem de Serviço (mesmas da ação Criar O.S.)
import { useWorkflowServiceOrderSources } from '@/hooks/useWorkflowServiceOrderSources';

/** v1.1.0: módulos de tarefa e seus rótulos (agrupamento das opções) */
const MODULOS_TAREFA = [
  { tipo: 'normal', rotulo: 'Tarefas' },
  { tipo: 'retention', rotulo: 'Retenção' },
  { tipo: 'legal', rotulo: 'Jurídico' },
] as const;

/** Opção exibida em um seletor de valor do filtro */
export interface OpcaoDeFiltro {
  value: string;
  label: string;
  /** Agrupamento opcional (ex.: workspace · pipeline) */
  grupo?: string;
}

export interface WorkflowFilterSources {
  /** Retorna as opções de uma fonte (lista vazia quando desconhecida) */
  opcoes: (fonte?: FonteDeOpcoes) => OpcaoDeFiltro[];
  /** Traduz um valor gravado (uuid/código) para o rótulo legível */
  rotuloDoValor: (fonte: FonteDeOpcoes | undefined, valor: string) => string;
  isLoading: boolean;
}

/**
 * @param entidade v1.2.0 — entidade-raiz do processo. Em processos de Tarefa,
 *        Retenção ou Jurídico as fontes de tarefa mostram somente os dados
 *        daquele módulo (os três são estruturas separadas).
 */
export function useWorkflowFilterSources(entidade?: string | null): WorkflowFilterSources {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  // v1.2.0: tipo de tarefa do processo (null quando não é módulo de tarefas)
  const tipoTarefa = taskTypeDaEntidade(entidade);

  const param = useWorkflowParamSources(entidade);
  const { data: dealTags = [] } = useDealTags(tenantId);
  const { data: contactTags = [] } = useContactTags(tenantId);
  const { activeSources } = useLeadSources();
  const { activeReasons } = useCrmLossReasons();
  const { products } = useCrmProducts();
  const { categories } = useCrmCategories();
  const { priceTables } = useCrmPriceTables();
  // v1.5.0: inclui inativos para que regras antigas continuem exibindo o nome
  const { types: tiposProposta } = useCrmProposalTypes(true);

  // v1.1.0: categorias dos TRÊS módulos de tarefa (o hook filtra por módulo
  // conforme a rota; aqui cada módulo é carregado explicitamente)
  const { categories: categoriasNormal = [] } = useTaskCategories(true, 'normal');
  const { categories: categoriasRetencao = [] } = useTaskCategories(true, 'retention');
  const { categories: categoriasJuridico = [] } = useTaskCategories(true, 'legal');
  // v1.2.0: fases do módulo do processo (antes seguia a rota, que no designer
  // é sempre "Tarefas" e misturava os módulos)
  const { phases: fasesTarefa = [] } = useTaskPhases(undefined, tipoTarefa ?? 'normal');
  const { reasons: motivosRetencao = [] } = useTaskCompletionReasons('retention', true);
  const { reasons: motivosJuridico = [] } = useTaskCompletionReasons('legal', true);

  // v1.1.0: classificações/motivos dos três módulos (a RPC exige p_task_type,
  // então cada módulo é consultado separadamente e agrupado pelo rótulo)
  // v1.2.0: em processos de tarefa, só o módulo do processo é consultado.
  const modulosConsultados = tipoTarefa
    ? MODULOS_TAREFA.filter((m) => m.tipo === tipoTarefa)
    : MODULOS_TAREFA;

  const { data: classificacoes = [] } = useQuery({
    queryKey: ['workflow-filter-task-classifications', tenantId, tipoTarefa ?? 'todos'],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async (): Promise<OpcaoDeFiltro[]> => {
      const listas = await Promise.all(
        modulosConsultados.map(async ({ tipo, rotulo }) => {
          const { data, error } = await (supabase.rpc as any)('get_task_classifications', {
            p_only_active: true,
            p_task_type: tipo,
          });
          if (error) return [] as OpcaoDeFiltro[];
          return ((data ?? []) as Array<{ id: string; name: string }>).map((c) => ({
            value: c.id,
            label: c.name,
            grupo: rotulo,
          }));
        }),
      );
      return listas.flat();
    },
  });

  /** Mapa fonte -> lista de opções, memorizado para não recriar a cada render */
  // v1.4.0: tipos de serviço, categorias, fases e prestadores da O.S.
  const os = useWorkflowServiceOrderSources(entidade);

  const mapa = useMemo<Record<FonteDeOpcoes, OpcaoDeFiltro[]>>(() => {
    // Workspaces / pipelines / fases vêm da árvore já carregada pelo designer
    const workspaces: OpcaoDeFiltro[] = param.crm_hierarquia.map((ws) => ({
      value: ws.id,
      label: ws.name,
    }));

    const pipelines: OpcaoDeFiltro[] = param.crm_hierarquia.flatMap((ws) =>
      ws.pipelines.map((p) => ({ value: p.id, label: p.name, grupo: ws.name })),
    );

    return {
      workspaces,
      pipelines,
      phases: param.crm_pipeline_phases.map((f) => ({ value: f.value, label: f.label, grupo: f.grupo })),
      sellers: param.crm_sellers,
      members: param.tenant_members,
      deal_tags: (dealTags as any[]).map((t) => ({ value: t.id, label: t.name })),
      contact_tags: (contactTags as any[]).map((t) => ({ value: t.id, label: t.name })),
      lead_sources: (activeSources ?? []).map((s: any) => ({ value: s.id, label: s.label })),
      loss_reasons: (activeReasons ?? []).map((r: any) => ({ value: r.id, label: r.name })),
      products: (products ?? []).map((p: any) => ({
        value: p.id,
        label: p.name,
        grupo: p.category?.name,
      })),
      product_categories: (categories ?? []).map((c: any) => ({ value: c.id, label: c.name })),
      price_tables: (priceTables ?? []).map((t: any) => ({ value: t.id, label: t.name })),
      // v1.2.0: categorias apenas do módulo do processo (Tarefa/Retenção/Jurídico)
      task_categories: [
        ...(!tipoTarefa || tipoTarefa === 'normal'
          ? categoriasNormal.map((c: any) => ({ value: c.id, label: c.name, grupo: 'Tarefas' }))
          : []),
        ...(!tipoTarefa || tipoTarefa === 'retention'
          ? categoriasRetencao.map((c: any) => ({ value: c.id, label: c.name, grupo: 'Retenção' }))
          : []),
        ...(!tipoTarefa || tipoTarefa === 'legal'
          ? categoriasJuridico.map((c: any) => ({ value: c.id, label: c.name, grupo: 'Jurídico' }))
          : []),
      ],
      task_classifications: classificacoes,
      kanban_phases: (fasesTarefa ?? []).map((p: any) => ({
        value: p.id,
        label: p.name,
        grupo: p.category_name,
      })),
      // v1.2.0: motivos apenas do módulo do processo
      task_completion_reasons: [
        ...(!tipoTarefa || tipoTarefa === 'retention'
          ? motivosRetencao.map((m: any) => ({ value: m.id, label: m.name, grupo: 'Retenção' }))
          : []),
        ...(!tipoTarefa || tipoTarefa === 'legal'
          ? motivosJuridico.map((m: any) => ({ value: m.id, label: m.name, grupo: 'Jurídico' }))
          : []),
      ],
      // v1.3.0: entidade Veículo (listas já carregadas pelas fontes de parâmetros)
      vehicle_statuses: param.vehicle_statuses,
      checklist_templates: param.checklist_templates,
      // v1.4.0: Ordens de serviço vinculadas ao negócio
      service_types: os.service_types,
      service_order_categories: os.service_order_categories,
      service_order_phases: os.service_order_phases,
      service_providers: os.service_providers,
      // v1.5.0: tipos de proposta
      proposal_types: (tiposProposta ?? []).map((t) => ({
        value: t.id,
        label: t.is_active ? t.name : `${t.name} (inativo)`,
      })),
    };
  }, [
    param.crm_hierarquia,
    param.crm_pipeline_phases,
    param.crm_sellers,
    param.tenant_members,
    dealTags,
    contactTags,
    activeSources,
    activeReasons,
    products,
    categories,
    priceTables,
    categoriasNormal,
    categoriasRetencao,
    categoriasJuridico,
    fasesTarefa,
    classificacoes,
    motivosRetencao,
    motivosJuridico,
    tipoTarefa,
    param.vehicle_statuses,
    param.checklist_templates,
    os,
    tiposProposta,
  ]);

  return {
    opcoes: (fonte) => (fonte ? mapa[fonte] ?? [] : []),
    rotuloDoValor: (fonte, valor) => {
      if (!fonte) return valor;
      const achado = (mapa[fonte] ?? []).find((o) => o.value === valor);
      // UUID sem correspondência: mostra apenas 8 caracteres em maiúsculas
      if (achado) return achado.label;
      return valor.length === 36 ? valor.slice(0, 8).toUpperCase() : valor;
    },
    isLoading: param.isLoading,
  };
}
